import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Modal, message } from "antd";
import CustomInput from "../Form/Input";
import CustomButton from "../Form/CustomButton";
import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import { verifyOTP, requestOTP } from "../../store/thunks/authThunks";
import showToast from "../../utils/toastNotifications";

const OtpVerification = ({ isOpen, onClose, email, onVerified, onBack }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(59);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
  } = useForm(
    {
      otp1: "",
      otp2: "",
      otp3: "",
      otp4: "",
    },
    {
      otp1: true,
      otp2: true,
      otp3: true,
      otp4: true,
    },
    {
      otp1: COMMON_FIELD_TYPES.NUMBER_FIELD,
      otp2: COMMON_FIELD_TYPES.NUMBER_FIELD,
      otp3: COMMON_FIELD_TYPES.NUMBER_FIELD,
      otp4: COMMON_FIELD_TYPES.NUMBER_FIELD,
    }
  );

  useEffect(() => {
    if (isOpen) {
      setResendTimer(0);
    }
  }, [isOpen]);

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev === 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  // Combine OTP digits
  const getOtp = () =>
    `${formData.otp1}${formData.otp2}${formData.otp3}${formData.otp4}`;

  const handleOtpChange = (name, value) => {
    if (value.length > 1) value = value.slice(-1); // only allow single digit

    handleInputChange(name, value);

    // Move focus after DOM update
    if (value) {
      const nextMap = { otp1: "otp2", otp2: "otp3", otp3: "otp4" };
      const nextName = nextMap[name];
      if (nextName) {
        setTimeout(() => {
          const nextInput = document.getElementsByName(nextName)[0];
          if (nextInput) nextInput.focus();
        }, 10); // small delay allows DOM update
      }
    }
  };

  // Handle OTP submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ otp1: true, otp2: true, otp3: true, otp4: true });

    if (validateForm()) {
      setLoading(true);
      try {
        const otp = getOtp();
        const response = await dispatch(
          verifyOTP({ contact: email, otp })
        ).unwrap();
        message.success("OTP verified successfully!");
        onVerified && onVerified(otp);
        showToast("success", "OTP verified successfully!");
      } catch (error) {
        message.error(
          error.message || "Failed to verify OTP. Please try again."
        );
        showToast("error", "Failed to verify OTP. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle Resend OTP
  const handleResend = async () => {
    setLoading(true);
    try {
      await dispatch(requestOTP({ emailOrPhone: email })).unwrap();
      showToast("success", "OTP resent successfully!");
      setResendTimer(59); // This will now be handled by useEffect
    } catch (error) {
      showToast("error", error.message || "Failed to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={500}
      centered
      className="confirmation-modal relative"
      style={{
        backgroundColor: "#F6FCFF",
        borderRadius: "40px",
      }}
    >
      <div className="h-[400px] sm:h-[450px] max-w-[547px] flex flex-col items-center justify-start my-4 sm:my-8">
        <h1 className="text-auth-level-1 font-semibold mb-4 font-poppins">
          <span className="text-roselea-green">Enter </span>
          <span className="text-roselea-pink">OTP</span>
        </h1>

        <p className="font-poppins text-[14px] sm:text-[16px] text-text-roselea-gray text-center mb-10">
          Check your email for a 4-digit code
        </p>

        <form className="w-full" onSubmit={handleSubmit}>
          <div className="w-full flex flex-col items-center">
            <div className="w-full flex justify-center items-center gap-2 sm:gap-4 mb-10">
              {["otp1", "otp2", "otp3", "otp4"].map((name, idx) => (
                <CustomInput
                  key={name}
                  name={name}
                  type="text"
                  maxLength={1}
                  value={formData[name]}
                  error={touched[name] && errors[name]}
                  onChange={(e) =>
                    handleOtpChange(name, e.target.value.replace(/\D/, ""))
                  }
                  onBlur={() => handleBlur(name, formData[name])}
                  onFocus={() => clearError(name)}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !formData[name]) {
                      const prevMap = {
                        otp4: "otp3",
                        otp3: "otp2",
                        otp2: "otp1",
                      };
                      const prevName = prevMap[name];
                      if (prevName) {
                        const prevInput =
                          document.getElementsByName(prevName)[0];
                        if (prevInput) {
                          prevInput.focus();
                        }
                      }
                    }
                  }}
                  className="w-16 h-16 sm:w-20 sm:h-20 text-center text-roselea-biscay-2 text-[30px] md:text-[34px] border border-button-roselea-green rounded-[10px] sm:rounded-[28px] font-bold font-inter"
                  inputStyle="bg-input-roselea-green bg-opacity-[0.03] text-center"
                  autoFocus={idx === 0}
                  isRequired={true}
                  centered={true}
                  otp={true}
                />
              ))}
            </div>
            {Object.keys(errors).length > 0 && (
              <p className="text-red-500">
                {Object.values(errors)[0] ||
                Object.values(errors)[1] ||
                Object.values(errors)[2] ||
                Object.values(errors)[3]
                  ? "All fields are required"
                  : ""}
              </p>
            )}
          </div>

          <div className="absolute bottom-[30px] sm:bottom-[60px] left-0 w-full p-[20px]">
            <CustomButton
              type="submit"
              className="h-[50px] sm:h-[45px] bg-roselea-pink text-roselea-white font-poppins font-semibold text-[16px] leading-[28px] rounded-[12px]"
              buttonText="Continue"
              loading={loading}
              disabled={loading}
            />
            <div className="flex flex-col items-center mt-4">
              <span className="text-sm text-gray-500">
                Didn&apos;t Receive The Code?{" "}
                <button
                  type="button"
                  className="text-[#4772F1] underline"
                  onClick={handleResend}
                  disabled={resendTimer > 0}
                >
                  {resendTimer > 0 ? (
                    <span className="font-medium underline text-button-roselea-green font-poppins text-[12px] sm:text-[14px]">
                      Resend in {resendTimer}s
                    </span>
                  ) : (
                    <span className="font-medium underline text-button-roselea-green font-poppins text-[12px] sm:text-[14px]">
                      Resend Now
                    </span>
                  )}
                </button>
              </span>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default OtpVerification;
